import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import toast from 'react-hot-toast';
import PasswordRules from './components/PasswordRules';
import { mapSupabaseErrorMessage } from './authErrors';
import AnimatedBackground from '../../components/AnimatedBackground';
import Logo from '../../components/Logo';
import SupportChat from '../../components/SupportChat';
import { Eye, EyeOff } from 'lucide-react';

const NewPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation() as any;
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const email = location?.state?.email as string | undefined;
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const rules = useMemo(() => ({
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    number: /\d/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
    match: confirmPassword.length > 0 && password === confirmPassword,
  }), [password, confirmPassword]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rules.length || !rules.uppercase || !rules.number || !rules.special || !rules.match) {
      toast.error('Password does not meet requirements');
      return;
    }
    // Require a valid recovery session set by verifyOtp
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData?.session) {
      toast.error('No valid reset session. Restart reset process.');
      return;
    }
    setIsLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      toast.error(mapSupabaseErrorMessage(error.message));
      setIsLoading(false);
      return;
    }
    toast.success('Password updated');
    navigate('/login');
    setIsLoading(false);
  };

  return (
    <div className="relative min-h-screen w-full flex items-start justify-center bg-[#0d1830] pt-20">
      <AnimatedBackground />
      <style>{`
        /* Override autofill styles for all states */
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-background-clip: text !important;
          -webkit-text-fill-color: white !important;
          transition: background-color 5000s ease-in-out 0s !important;
          box-shadow: inset 0 0 0 1000px rgba(255, 255, 255, 0.08) !important;
          caret-color: white !important;
        }
        
        /* Ensure the fields maintain styling when typing after autofill */
        .new-password-input {
          background: rgba(255, 255, 255, 0.08) !important;
          backdrop-filter: blur(5px) !important;
          -webkit-backdrop-filter: blur(5px) !important;
          color: white !important;
          -webkit-text-fill-color: white !important;
        }
      `}</style>
      <div className="relative z-10 w-full max-w-2xl flex flex-col items-center">
        <div className="mb-4 pb-8 w-full">
          <Logo />
        </div>
        <div
          className="relative p-8 rounded-2xl w-full max-w-md"
          style={{
            background: 'rgba(0, 0, 0, 0.01)',
            backdropFilter: 'blur(3px) saturate(120%)',
            WebkitBackdropFilter: 'blur(3px) saturate(120%)',
            border: '0.5px solid rgba(255, 255, 255, 0.04)',
            boxShadow: '0 15px 32px 0 rgba(255, 255, 255, 0.2)'
          }}
        >
          <div className="text-center mb-8">
            <h2 className="text-2xl font-light text-white/90 tracking-wide">Set New Password</h2>
          </div>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="New password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="new-password-input w-full px-3 py-2.5 pr-10 rounded-lg text-white placeholder-white/90 border border-white/30 transition-all duration-300 focus:border-[#17c480] focus:ring-2 focus:ring-[#17c480] focus:outline-none"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/70 hover:text-white transition-colors focus:outline-none"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="new-password-input w-full px-3 py-2.5 pr-10 rounded-lg text-white placeholder-white/90 border border-white/30 transition-all duration-300 focus:border-[#17c480] focus:ring-2 focus:ring-[#17c480] focus:outline-none"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/70 hover:text-white transition-colors focus:outline-none"
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <PasswordRules rules={rules} />
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 rounded-lg font-semibold tracking-wide transition-all duration-300 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: 'rgba(23, 196, 128, 0.9)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                color: '#0d1830'
              }}
            >
              {isLoading ? 'Updating…' : 'Update password'}
            </button>
          </form>
          <div className="mt-4 text-white text-xs text-center">
            <p>Need help?  Contact <a href="mailto:support@engramiq.com" className="underline hover:text-[#17c480] transition-colors">support@engramiq.com</a> for assistance</p>
          </div>
        </div>
      </div>
      {/* <SupportChat /> */}
    </div>
  );
};

export default NewPasswordPage;

